import Box from '@mui/material/Box';
import { red } from '@mui/material/colors';
import ListItemButton from '@mui/material/ListItemButton';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Ellipsis } from '@openx/components-core/lib/Ellipsis/Ellipsis.tsx';
import { StatusIndicator } from '@openx/components-core/lib/StatusIndicator/StatusIndicator.tsx';
import { StyledAddIcon, StyledRemoveIcon } from '@openx/components-core/lib/TransferList/components/Icons.styled.tsx';
import type { SeedListData } from '@openx/types/segments.ts';
import { abbreviatedNumber } from '@openx/utils/lib/abbreviatedNumber.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReachTooltip } from '../../../components/ReachTooltip/ReachTooltip.tsx';
import { SeedExpirationAlerts } from '../../../components/SeedExpirationAlerts/SeedExpirationAlerts.tsx';
import { DateCell } from '../../../SegmentsList/parts/DateCell.tsx';
import { mapStatus } from '../../../utils/mapStatus.ts';
import { validateSeedsAllowedStatus } from '../../validationSchema.ts';

interface SeedRenderItemProps {
  item: SeedListData;
  style: React.CSSProperties;
  isDisabled?: boolean;
  handleRowClick?: (item: SeedListData) => void;
  hideActionIcon?: boolean;
  disableTouchRipple?: boolean;
  showExpirationAlerts?: boolean;
}

const notReadySx = () => ({
  bgcolor: alpha(red.A700, 0.25),
  '&:hover': {
    bgcolor: alpha(red.A700, 0.13),
  },
});

export const SeedRenderItem = ({
  item,
  style,
  isDisabled = false,
  handleRowClick,
  hideActionIcon,
  disableTouchRipple = false,
  showExpirationAlerts = false,
}: SeedRenderItemProps) => {
  const status = mapStatus(item.status);
  const { t } = useTranslation();

  const isAllowedStatus = validateSeedsAllowedStatus([item]);
  const isDisabledComputed = isDisabled || (!isAllowedStatus && !item.isOnList);

  const numOfVisibleItemFooterFields = [item.account, item.updated, item.created].filter(Boolean).length;

  const itemFooterFieldStyles = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      maxWidth: `${100 / numOfVisibleItemFooterFields}%`,
      overflow: 'hidden',
    }),
    [numOfVisibleItemFooterFields]
  );

  return (
    <ListItemButton
      style={style}
      disabled={isDisabledComputed}
      disableTouchRipple={disableTouchRipple}
      onClick={() => handleRowClick?.(item)}
      sx={theme => ({
        userSelect: 'auto',
        ...(!isAllowedStatus && item.isOnList ? notReadySx() : {}),
        '&:hover .add-icon-circle': {
          background: alpha(theme.palette.common.black, 0.05),
          display: 'flex',
          p: 1,
        },
      })}
    >
      {!isDisabledComputed && !hideActionIcon && (
        <Box
          className="add-icon-circle"
          sx={{
            position: 'absolute',
            top: '50%',
            right: 8,
            zIndex: 2,
            borderRadius: '50%',
            transform: 'translateY(-50%)',
            cursor: 'pointer',
            p: 0.5,
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            transition: 'background 0.2s',
          }}
          onClick={e => {
            e.stopPropagation();
            handleRowClick?.(item);
          }}
        >
          {item.isOnList ? <StyledRemoveIcon data-test="RemoveIcon" /> : <StyledAddIcon data-test="AddIcon" />}
        </Box>
      )}
      <Box sx={{ width: '100%', px: 0 }}>
        <Typography
          variant="body1Bold"
          gutterBottom
          sx={{
            maxWidth: '100%',
          }}
        >
          <Ellipsis width="100%" tooltip>
            {item.name}
          </Ellipsis>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          {status && (
            <Box sx={{ mr: 2, ml: 1 }} component="span">
              <StatusIndicator
                label={status.label}
                status={status.status}
                wrapperStyles={{ display: 'flex', alignItems: 'center' }}
                textStyles={{ wordBreak: 'unset' }}
              />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: 1.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mr: 1,
              }}
            >
              {t('Reach')}:
            </Typography>
            <Typography component="span">{abbreviatedNumber(item.reach)}</Typography>
            <ReachTooltip id={item.id} reach={item.reach} sx={{ ml: 0 }} />
          </Box>
          {item.provider && (
            <>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mr: 0.5,
                }}
              >
                {t('Provider')}:
              </Typography>
              <Typography variant="body2" sx={{ mr: 2 }}>
                {item.provider}
              </Typography>
            </>
          )}
          {showExpirationAlerts && <SeedExpirationAlerts segment={item} />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {item.account && (
            <Box sx={itemFooterFieldStyles}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 11,
                  color: 'text.secondary',
                  mr: 0.5,
                }}
              >
                {t('Organization')}:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 11,
                  overflow: 'hidden',
                }}
              >
                <Ellipsis tooltip width="100%">
                  {item.account}
                </Ellipsis>
              </Typography>
            </Box>
          )}
          {item.updated && (
            <Box sx={itemFooterFieldStyles}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 11,
                  color: 'text.secondary',
                  mr: 0.5,
                }}
              >
                {t('Updated')}:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 11,
                  overflow: 'hidden',
                }}
              >
                <Ellipsis width="100%">
                  <DateCell date={item.updated} sx={{ fontSize: 11 }} />
                </Ellipsis>
              </Typography>
            </Box>
          )}
          {item.created && (
            <Box sx={itemFooterFieldStyles}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 11,
                  color: 'text.secondary',
                  mr: 0.5,
                }}
              >
                {t('Created')}:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 11,
                  overflow: 'hidden',
                }}
              >
                <Ellipsis width="100%">
                  <DateCell date={item.created} sx={{ fontSize: 11 }} />
                </Ellipsis>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </ListItemButton>
  );
};
